import { useCallback } from 'react';

import {
  useTrackFailedToUploadAsset,
  useTrackUploadAborted,
  useTrackUploadCompleted,
  useTrackUploadError,
  useTrackUploadedAsset,
  useTrackUploadStarted,
} from '@air/analytics';
import { ClipSource } from '@air/api/types';
import { isLarge, uploadByIdSelector } from '@air/redux-uploader';
import { type UseUploadManagerProps, useUploadManager } from '@air/upload-manager';
import { isAbortError } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type UploadTaskParams } from '~/components/Upload/hooks/types';
import { useUploadConcurrencyChangeAnalytics } from '~/components/Upload/hooks/useUploadConcurrencyChangeAnalytics';
import { useFetchClipForContentCollectionUpload } from '~/components/Upload/publicUpload/useFetchClipForContentCollectionUpload';
import { useGetLargePublicUploadTasks } from '~/components/Upload/publicUpload/useGetLargePublicUploadTasks';
import { useGetSmallPublicUploadTask } from '~/components/Upload/publicUpload/useGetSmallPublicUploadTask';
import {
  getContentCollectionUploadAnalyticsParams,
  getUploadAbortedAnalyticsParams,
  getUploadCompletedAnalyticsParams,
  getUploadErrorAnalyticsParams,
  getUploadStartedAnalyticsParams,
} from '~/components/Upload/utils/uploadAnalytics';
import { useAirStore } from '~/utils/ReduxUtils';

export const useContentCollectionUploadsManager = () => {
  const store = useAirStore();
  const { fetchClipForContentCollectionUpload } = useFetchClipForContentCollectionUpload();
  const { trackUploadedAsset } = useTrackUploadedAsset();
  const { trackFailedToUploadAsset } = useTrackFailedToUploadAsset();
  const { trackUploadAborted } = useTrackUploadAborted();
  const { trackUploadCompleted } = useTrackUploadCompleted();
  const { trackUploadError } = useTrackUploadError();
  const { trackUploadStarted } = useTrackUploadStarted();

  const { getSmallUploadTask } = useGetSmallPublicUploadTask();
  const { getLargeUploadTasks } = useGetLargePublicUploadTasks();
  const { onConcurrencyChange } = useUploadConcurrencyChangeAnalytics({ source: 'content_collection' });

  const getUploadTasks = useCallback<UseUploadManagerProps['getUploadTasks']>(
    ({ upload }) => {
      const params: UploadTaskParams = {
        uploadId: upload.id,
        onUploadFinished: ({ uploadId, uploadUrlType }) => {
          const upload = uploadByIdSelector(store.getState(), uploadId);

          if (upload) {
            try {
              trackUploadedAsset({ clipId: upload.clipId });

              const analyticsParams = getContentCollectionUploadAnalyticsParams({
                context: 'content_collection_upload_manager',
                state: store.getState(),
                upload,
              });
              trackUploadCompleted(getUploadCompletedAnalyticsParams(analyticsParams, upload, { uploadUrlType }));
            } catch (analyticsError) {
              reportErrorToBugsnag({ error: analyticsError, context: 'Failed to track upload completed analytics' });
            }
          }
        },
        onUploadStarted: ({ maxRetryCount, retryCount, uploadId, uploadUrlType }) => {
          const upload = uploadByIdSelector(store.getState(), uploadId);

          if (upload) {
            try {
              const analyticsParams = getContentCollectionUploadAnalyticsParams({
                context: 'content_collection_upload_manager',
                state: store.getState(),
                upload,
              });

              trackUploadStarted(
                getUploadStartedAnalyticsParams(analyticsParams, upload, { maxRetryCount, retryCount, uploadUrlType }),
              );
            } catch (analyticsError) {
              reportErrorToBugsnag({ error: analyticsError, context: 'Failed to track upload started analytics' });
            }
          }

          return fetchClipForContentCollectionUpload(uploadId);
        },
        onError: ({ upload, error, failedStage, uploadUrlType }) => {
          const knownError = reportErrorToBugsnag({ error, context: 'Error uploading file' });

          if (!isAbortError(knownError)) {
            trackFailedToUploadAsset({
              id: upload.id,
              type: upload.apiUploadData.type,
              source: ClipSource.web,
              error: knownError.message,
            });
          }

          try {
            const analyticsParams = getContentCollectionUploadAnalyticsParams({
              context: 'content_collection_upload_manager',
              state: store.getState(),
              upload,
            });

            if (isAbortError(knownError)) {
              trackUploadAborted(
                getUploadAbortedAnalyticsParams({ commonParams: analyticsParams, failedStage, upload }),
              );
            } else {
              trackUploadError(
                getUploadErrorAnalyticsParams({
                  commonParams: analyticsParams,
                  error: knownError,
                  failedStage,
                  upload,
                  uploadUrlType,
                }),
              );
            }
          } catch (analyticsError) {
            reportErrorToBugsnag({ error: analyticsError, context: 'Failed to track upload error analytics' });
          }
        },
      };

      if (isLarge(upload)) {
        return getLargeUploadTasks(params);
      } else {
        return [getSmallUploadTask(params)];
      }
    },
    [
      fetchClipForContentCollectionUpload,
      getLargeUploadTasks,
      getSmallUploadTask,
      store,
      trackFailedToUploadAsset,
      trackUploadAborted,
      trackUploadCompleted,
      trackUploadError,
      trackUploadStarted,
      trackUploadedAsset,
    ],
  );

  return useUploadManager({ getUploadTasks, onConcurrencyChange });
};
